// ConfigTab.js
import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { fetchConfigData, baseURL, dateTemplate, fetchJenkinsPlans, handleUnauthorized } from './DataFlow';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'primereact/inputswitch';

const ConfigTab = () => {
    const [triggerConfig, setTriggerConfig] = useState([]);
    const [schedulerConfig, setSchedulerConfig] = useState([]);
    const [selectedTriggerConfig, setSelectedTriggerConfig] = useState([]);
    const [selectedSchedulerConfig, setSelectedSchedulerConfig] = useState([]);
    const [editingRowData, setEditingRowData] = useState({});
    const toast = useRef(null);
    const [jenkinsPlans, setJenkinsPlans] = useState([]);
    const [configDialogVisible, setConfigDialogVisible] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [configType, setConfigType] = useState('schedule');
    const [paramValues, setParamValues] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [branchPattern, setBranchPattern] = useState('.*');
    const [testSuites, setTestSuites] = useState([]);
    const [priority, setPriority] = useState(2);
    const overlayPanel = useRef(null);

    useEffect(() => {
        fetchConfigData();
        fetchJenkinsPlans(setJenkinsPlans, handleUnauthorized);
    }, []);

    const fetchConfigData = async () => {
        try {
            const [schedulerResponse, triggerResponse] = await Promise.all([
                fetch(`${baseURL}/api/config/scheduler-config`, {
                    headers: { 'api_token': localStorage.getItem('apiToken') }
                }),
                fetch(`${baseURL}/api/config/trigger-config`, {
                    headers: { 'api_token': localStorage.getItem('apiToken') }
                })
            ]);

            if (schedulerResponse.ok) {
                const schedulerData = await schedulerResponse.json();
                setSchedulerConfig(schedulerData);
            }

            if (triggerResponse.ok) {
                const triggerData = await triggerResponse.json();
                setTriggerConfig(triggerData);
            }
        } catch (error) {
            console.error('Error fetching configs:', error);
        }
    };

    // Determine if the user is an admin
    const isAdmin = localStorage.getItem('isAppPowerUser') === 'true';

    const showErrorMessages = (messages) => {
        if (toast.current) {
            messages.forEach((msg) => {
                toast.current.show({ severity: 'error', summary: 'Validation Error', detail: msg, life: 3000 });
            });
        }
    };

    const isFieldValid = (field, value) => {
        if (field === 'startTime') {
            return !!value;
        }
        if (field === 'branch' || field === 'testType') {
            return value && value.trim() !== '';
        }
        return true;
    };

    // Unique ID generator
    const createId = () => {
        return '_' + Math.random().toString(36).substr(2, 9);
    };

    // Options for platforms
    const periodOptions = [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
    ];

    const dayOfWeekOptions = [
        { label: 'Sunday', value: 'Sunday' },
        { label: 'Monday', value: 'Monday' },
        { label: 'Tuesday', value: 'Tuesday' },
        { label: 'Wednesday', value: 'Wednesday' },
        { label: 'Thursday', value: 'Thursday' },
        { label: 'Friday', value: 'Friday' },
        { label: 'Saturday', value: 'Saturday' },
    ];

    // Common editor functions
    const textEditor = (options) => {
        const isValid = isFieldValid(options.field, options.value);
        return (
            <div>
                <InputText
                    value={options.value || ''}
                    onChange={(e) => {
                        options.editorCallback(e.target.value);
                    }}
                    className={classNames({ 'p-invalid': !isValid })}
                />
            </div>
        );
    };

    const numberEditor = (options) => (
        <InputNumber
            value={options.value || 0}
            onValueChange={(e) => {
                options.editorCallback(e.value);
                updateEditingRowData(options.field, e.value);
            }}
        />
    );

    const dropdownEditor = (options, dropdownOptions) => (
        <Dropdown
            value={options.value}
            options={dropdownOptions}
            onChange={(e) => {
                options.editorCallback(e.value);
                updateEditingRowData(options.field, e.value);
            }}
            placeholder="Select"
        />
    );

    const timeEditor = (options) => {
        const isValid = isFieldValid(options.field, options.value);
        return (
            <div>
                <Calendar
                    value={options.value || null}
                    onChange={(e) => {
                        options.editorCallback(e.value);
                    }}
                    timeOnly
                    hourFormat="24"
                    className={classNames({ 'p-invalid': !isValid })}
                />
            </div>
        );
    };

    const checkboxEditor = (options) => (
        <Checkbox
            checked={options.value || false}
            onChange={(e) => {
                options.editorCallback(e.checked);
                updateEditingRowData(options.field, e.checked);
            }}
        />
    );

    // Function to update editing row data
    const updateEditingRowData = (field, value) => {
        setEditingRowData((prevData) => {
            const updatedData = { ...prevData, [field]: value };

            if (field === 'period') {
                const periodValue = value.toLowerCase();
                if (periodValue === 'weekly') {
                    // Check if 'startDay' is a valid day of the week
                    const validDaysOfWeek = dayOfWeekOptions.map(option => option.value);
                    if (!validDaysOfWeek.includes(updatedData.startDay)) {
                        // 'startDay' is invalid, set to 'Monday'
                        updatedData.startDay = 'Monday';
                    }
                } else if (periodValue === 'monthly') {
                    // For 'Monthly' period, ensure 'startDay' is a valid number between 1 and 31
                    let day = parseInt(updatedData.startDay, 10);
                    if (isNaN(day) || day < 1 || day > 31) {
                        day = 1;
                    }
                    updatedData.startDay = day.toString();
                }
            }

            return updatedData;
        });
    };

    // Add new row functions
    const addTestConfigRow = () => {
        addNewConfig('trigger');
    };

    const addSchedulerConfigRow = () => {
        addNewConfig('schedule');
    };

    const onSchedulerRowEditComplete = async (e) => {
        let updatedData = { ...e.newData };

        // Ensure period value is lowercase
        if (updatedData.period) {
            updatedData.period = updatedData.period.toLowerCase();
        }

        // Validate required fields
        const validationErrors = [];

        // Validate 'startTime'
        if (!updatedData.startTime) {
            validationErrors.push('Start Time is required.');
        }

        // Validate 'branch'
        if (!updatedData.branch || updatedData.branch.trim() === '') {
            validationErrors.push('Branch Pattern is required.');
        }

        // Validate 'testType'
        if (!updatedData.testType || updatedData.testType.trim() === '') {
            validationErrors.push('Test Type is required.');
        }

        // If there are validation errors, display them and prevent saving
        if (validationErrors.length > 0) {
            showErrorMessages(validationErrors);
            // Keep the row in edit mode
            return;
        }

        // Existing logic to handle 'startDay' based on 'period'
        if (updatedData.period === 'monthly') {
            // Ensure 'startDay' is a valid number between 1 and 31
            let day = parseInt(updatedData.startDay, 10);
            if (isNaN(day) || day < 1 || day > 31) {
                day = 1;
            }
            updatedData.startDay = day.toString();
        } else if (updatedData.period === 'weekly') {
            // Ensure 'startDay' is a valid day of the week
            const validDaysOfWeek = dayOfWeekOptions.map(option => option.value);
            if (!validDaysOfWeek.includes(updatedData.startDay)) {
                updatedData.startDay = 'Monday';
            }
        }

        let updatedRows = [...schedulerConfig];
        updatedRows[e.index] = updatedData;
        setSchedulerConfig(updatedRows);
        setEditingRowData({});

        try {
            const authToken = localStorage.getItem('apiToken');
            const response = await fetch(`/api/config/scheduler-config/${updatedData._id}`, {
                method: 'PUT',
                headers: {
                    'api_token': authToken,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedData),
            });

            if (!response.ok) {
                throw new Error('Failed to update the scheduler configuration.');
            }
        } catch (error) {
            console.error('Error updating scheduler configuration:', error);
            // Optionally handle the error
        }
    };

    const onSchedulerRowEditInit = (e) => {
        setEditingRowData({ ...e.data });
    };

    const onSchedulerRowEditCancel = () => {
        setEditingRowData({});
    };

    // Conditional field rendering for Scheduler Config
    const renderConditionalField = (rowData, field) => {
        const isEditing = editingRowData._id === rowData._id;
        const period = (isEditing ? editingRowData.period : rowData.period)?.toLowerCase();
        const data = isEditing ? editingRowData : rowData;

        if (field === 'startDay') {
            switch (period) {
                case 'weekly':
                    return data.startDay; // Should be a day of the week
                case 'monthly':
                    return data.startDay; // Should be a day of the month (e.g., "1st", "15th")
                default:
                    return '';
            }
        }

        if (field === 'startTime' && period) {
            if (data.startTime) {
                const time = new Date(data.startTime);
                if (!isNaN(time)) {
                    return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                }
            }
        }

        return '';
    };

    const conditionalEditor = (options, field) => {
        const isEditing = editingRowData._id === options.rowData._id;
        const period = (isEditing ? editingRowData.period : options.rowData.period)?.toLowerCase();

        if (field === 'startDay') {
            if (period === 'weekly') {
                // Ensure 'startDay' is a valid day of the week
                const validDaysOfWeek = dayOfWeekOptions.map(option => option.value);
                let value = options.value;
                if (!validDaysOfWeek.includes(value)) {
                    value = 'Monday';
                    options.editorCallback(value);
                    updateEditingRowData(options.field, value);
                }
                // Dropdown for days of the week
                return (
                    <Dropdown
                        value={value}
                        options={dayOfWeekOptions}
                        onChange={(e) => {
                            options.editorCallback(e.value);
                            updateEditingRowData(options.field, e.value);
                        }}
                        placeholder="Select Day"
                    />
                );
            }
            if (period === 'monthly') {
                // InputNumber for day of the month (1-31)
                let value = parseInt(options.value, 10);
                if (isNaN(value) || value < 1 || value > 31) {
                    value = 1;
                    options.editorCallback(value);
                    updateEditingRowData(options.field, value);
                }
                return (
                    <InputNumber
                        value={value}
                        onValueChange={(e) => {
                            options.editorCallback(e.value);
                            updateEditingRowData(options.field, e.value);
                        }}
                        min={1}
                        max={31}
                        placeholder="Enter day (1-31)"
                    />
                );
            }
        }

        if (field === 'startTime' && period) {
            return timeEditor(options);
        }

        return null;
    };

    const addNewConfig = (type) => {
        setConfigType(type);
        setConfigDialogVisible(true);
        setSelectedPlan(null);
        setParamValues({});
    };

    const handlePlanSelection = (plan) => {
        setSelectedPlan(plan);
        // Initialize parameter values with defaults
        const initialValues = {};
        plan.parameters.forEach(param => {
            initialValues[param.name] = param.defaultParameterValue?.value || '';
        });
        setParamValues(initialValues);
    };

    const handleParamValueChange = (name, value) => {
        setParamValues(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const saveConfig = async () => {
        if (!selectedPlan) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a plan'
            });
            return;
        }

        const configData = {
            planId: selectedPlan._id,
            parameters: paramValues || {},
            enabled: editingRowData.enabled ?? true,
            priority: Number(editingRowData?.priority || 1),
            branch: editingRowData?.branch || 'develop'
        };

        if (configType === 'schedule') {
            configData.period = editingRowData?.period || 'daily';
            configData.startDay = editingRowData?.startDay || '';
            configData.startTime = editingRowData?.startTime || '';
            configData.isAgentQueue = editingRowData?.isAgentQueue || false;
        } else {
            configData.branchPattern = branchPattern;
            configData.testSuites = typeof testSuites === 'string' && testSuites.trim() 
                ? testSuites.split(',').map(suite => suite.trim()) 
                : [];
            configData.priority = Number(priority || editingRowData?.priority || 1);
        }

        try {
            const endpoint = configType === 'schedule' 
                ? 'scheduler-config'
                : 'trigger-config';

            const method = isEditing ? 'PUT' : 'POST';
            let url;

            if (method === 'PUT') {
                if (!editingRowData?._id) {
                    throw new Error('No ID found for update operation');
                }
                url = `${baseURL}/api/config/${endpoint}/${editingRowData._id}`;
                console.log('Updating existing config:', editingRowData._id);
            } else {
                url = `${baseURL}/api/config/${endpoint}`;
                console.log('Creating new config');
            }

            console.log('Making request:', {
                method,
                url,
                configData,
                editingRowData,
                isEditing
            });

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'api_token': localStorage.getItem('apiToken')
                },
                body: JSON.stringify(configData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save configuration');
            }

            const savedConfig = await response.json();
            
            if (isEditing) {
                if (configType === 'schedule') {
                    setSchedulerConfig(schedulerConfig.map(config => 
                        config._id === editingRowData._id ? savedConfig : config
                    ));
                } else {
                    setTriggerConfig(triggerConfig.map(config => 
                        config._id === editingRowData._id ? savedConfig : config
                    ));
                }
            } else {
                if (configType === 'schedule') {
                    setSchedulerConfig([...schedulerConfig, savedConfig]);
                } else {
                    setTriggerConfig([...triggerConfig, savedConfig]);
                }
            }

            setConfigDialogVisible(false);
            resetForm();
            
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: `Configuration ${isEditing ? 'updated' : 'saved'} successfully`
            });

            // Refresh the data after save
            fetchConfigData();
        } catch (error) {
            console.error('Error saving configuration:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || `Failed to ${isEditing ? 'update' : 'save'} configuration`
            });
        }
    };

    const resetForm = () => {
        setIsEditing(false);
        setEditingRowData({});
        setSelectedPlan(null);
        setParamValues({});
        setBranchPattern('.*');
        setTestSuites([]);
        setPriority(2);
    };

    const renderParameterField = (param) => {
        const { name, type, defaultParameterValue, choices } = param;
        const value = paramValues[name] || '';

        switch (type) {
            case "ChoiceParameterDefinition":
                return (
                    <div className="p-field">
                        <label htmlFor={name}>{name}</label>
                        <Dropdown
                            id={name}
                            value={value}
                            options={choices?.map(c => ({ label: c, value: c })) || []}
                            onChange={(e) => handleParamValueChange(name, e.value)}
                            placeholder={`Select ${name}`}
                            className="w-full"
                        />
                    </div>
                );

            case "CascadeChoiceParameter":
                return (
                    <div className="p-field">
                        <label htmlFor={name}>{name}</label>
                        <MultiSelect
                            id={name}
                            value={Array.isArray(value) ? value : value ? [value] : []}
                            options={choices?.map(c => ({ label: c, value: c })) || []}
                            onChange={(e) => handleParamValueChange(name, e.value)}
                            placeholder={`Select ${name}`}
                            className="w-full"
                            display="chip"
                            filter
                        />
                        {defaultParameterValue?.value && (
                            <small className="text-gray-500">
                                Default: {defaultParameterValue.value}
                            </small>
                        )}
                    </div>
                );

            case "BooleanParameterDefinition":
                return (
                    <div className="field-checkbox mt-2">
                        <Checkbox
                            inputId={`param_${name}`}
                            checked={Boolean(value)}
                            onChange={(e) => handleParamValueChange(name, e.checked)}
                        />
                        <label htmlFor={`param_${name}`}>{" "}{name}</label>
                    </div>
                );

            case "StringParameterDefinition":
                return (
                    <div className="p-field">
                        <label htmlFor={name}>{name}</label>
                        <InputText
                            id={name}
                            value={value}
                            onChange={(e) => handleParamValueChange(name, e.target.value)}
                            placeholder={defaultParameterValue?.value || `Enter ${name}`}
                            className="w-full"
                        />
                        {defaultParameterValue?.value && (
                            <small className="text-gray-500">
                                Default: {defaultParameterValue.value}
                            </small>
                        )}
                    </div>
                );

            case "NumberParameterDefinition":
                return (
                    <div className="p-field">
                        <label htmlFor={name}>{name}</label>
                        <InputNumber
                            id={name}
                            value={Number(value) || 0}
                            onValueChange={(e) => handleParamValueChange(name, e.value)}
                            className="w-full"
                        />
                        {defaultParameterValue?.value && (
                            <small className="text-gray-500">
                                Default: {defaultParameterValue.value}
                            </small>
                        )}
                    </div>
                );

            default:
                return (
                    <div className="p-field">
                        <label htmlFor={name}>{name}</label>
                        <InputText
                            id={name}
                            value={value}
                            onChange={(e) => handleParamValueChange(name, e.target.value)}
                            placeholder={`Enter ${name}`}
                            className="w-full"
                        />
                    </div>
                );
        }
    };

    // Updated onEditButtonClick function
    const onEditButtonClick = (rowData) => {
        console.log('Edit button clicked with data:', rowData); // Debug log
        
        setIsEditing(true);
        setConfigType(rowData.period ? 'schedule' : 'trigger');
        
        // Preserve all original data including _id
        setEditingRowData({
            _id: rowData._id, // Ensure _id is preserved
            ...rowData,
            period: rowData.period || 'daily',
            startDay: rowData.startDay || '',
            startTime: rowData.startTime || '',
            enabled: rowData.enabled
        });
        
        const plan = jenkinsPlans.find(p => p.name === rowData.planName);
        if (plan) {
            setSelectedPlan(plan);
            
            // Set parameters from planInstance if they exist
            if (rowData.planInstance?.parameters) {
                setParamValues(rowData.planInstance.parameters);
            } else {
                // Initialize with default values if no parameters are set
                const initialValues = {};
                plan.parameters.forEach(param => {
                    initialValues[param.name] = param.defaultParameterValue?.value || '';
                });
                setParamValues(initialValues);
            }

            // Set trigger-specific fields if it's a trigger config
            if (!rowData.period) {
                setBranchPattern(rowData.branchPattern || '.*');
                const suites = Array.isArray(rowData.testSuites) 
                    ? rowData.testSuites.join(', ')
                    : rowData.testSuites || '';
                setTestSuites(suites);
                setPriority(rowData.priority || 2);
            }
        }
        
        console.log('Editing state set:', {
            id: rowData._id,
            type: rowData.period ? 'schedule' : 'trigger',
            editingRowData: {
                _id: rowData._id,
                ...rowData
            }
        });
        
        setConfigDialogVisible(true);
    };


    // Add time format function
    const formatTime = (time) => {
        if (!time) return '';
        return time;
    };

    // Add period format function
    const formatPeriod = (period) => {
        if (!period) return '';
        return period.charAt(0).toUpperCase() + period.slice(1).toLowerCase();
    };

    const confirmDelete = (rowData, configType) => {
        confirmDialog({
            message: 'Are you sure you want to delete this configuration?',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => deleteConfig(rowData._id, configType),
            reject: () => {}
        });
    };

    const deleteConfig = async (id, type) => {
        try {
            const endpoint = type === 'schedule' ? '/api/config/scheduler-config' : '/api/config/trigger-config';
            const response = await fetch(`${baseURL}${endpoint}/${id}`, {
                method: 'DELETE',
                headers: {
                    'api_token': localStorage.getItem('apiToken')
                }
            });

            if (!response.ok) throw new Error('Failed to delete configuration');

            // Update local state
            if (type === 'schedule') {
                setSchedulerConfig(schedulerConfig.filter(config => config._id !== id));
            } else {
                setTriggerConfig(triggerConfig.filter(config => config._id !== id));
            }

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Configuration deleted successfully'
            });
        } catch (error) {
            console.error('Error deleting configuration:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete configuration'
            });
        }
    };

    // Add this helper function near the top of the component
    const formatLastExecutionDate = (date) => {
        if (!date) return 'Never';
        return new Date(date).toLocaleString();
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                {/* Trigger Config Table */}
                <div className="col-12">
                    <div className="card">
                        <DataTable
                            value={triggerConfig}
                            dataKey="_id"
                            paginator
                            rows={10}
                            showGridlines
                            className="mb-3"
                            header={
                                <div className="flex justify-content-between align-items-center">
                                    <h5 className="m-0">Trigger Configuration</h5>
                                    {isAdmin && (
                                        <Button
                                            icon="pi pi-plus"
                                            className="p-button-success"
                                            style={{ width: '100px' }}
                                            onClick={() => {
                                                setIsEditing(false);
                                                setConfigType('trigger');
                                                resetForm();
                                                setConfigDialogVisible(true);
                                            }}
                                        />
                                    )}
                                </div>
                            }
                        >
                            <Column field="planName" header="Plan" />
                            <Column field="branchPattern" header="Branch Pattern" />
                            <Column 
                                field="testSuites" 
                                header="Test Suites" 
                                body={(rowData) => Array.isArray(rowData.testSuites) ? 
                                    rowData.testSuites.join(', ') : rowData.testSuites || ''}
                            />
                            <Column field="priority" header="Priority" 
                                body={(rowData) => rowData.priority || 1} 
                            />
                            <Column 
                                field="enabled" 
                                header="Enabled" 
                                body={(rowData) => rowData.enabled ? 'Yes' : 'No'} 
                            />
                            <Column 
                                field="lastExecutionDate" 
                                header="Last Execution" 
                                sortable
                                body={(rowData) => formatLastExecutionDate(rowData.lastExecutionDate)}
                            />
                            {isAdmin && (
                                <Column
                                    header="Actions"
                                    body={(rowData) => (
                                        <div className="flex justify-content-center gap-2">
                                            <Button
                                                icon="pi pi-pencil"
                                                rounded
                                                outlined
                                                className="p-button-text"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onEditButtonClick(rowData);
                                                }}
                                                style={{ width: '2.5rem', height: '2.5rem' }}
                                            />
                                            <Button
                                                icon="pi pi-trash"
                                                rounded
                                                outlined
                                                severity="danger"
                                                className="p-button-text"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    confirmDelete(rowData, 'trigger');
                                                }}
                                                style={{ width: '2.5rem', height: '2.5rem' }}
                                            />
                                        </div>
                                    )}
                                />
                            )}
                        </DataTable>
                    </div>
                </div>

                {/* Scheduler Config Table */}
                <div className="col-12">
                    <div className="card">
                        <DataTable
                            value={schedulerConfig}
                            dataKey="_id"
                            paginator
                            rows={10}
                            showGridlines
                            header={
                                <div className="flex justify-content-between align-items-center">
                                    <h5 className="m-0">Scheduler Configuration</h5>
                                    {isAdmin && (
                                        <Button
                                            icon="pi pi-plus"
                                            className="p-button-success"
                                            style={{ width: '100px' }}
                                            onClick={() => {
                                                setIsEditing(false);
                                                setConfigType('schedule');
                                                resetForm();
                                                setConfigDialogVisible(true);
                                            }}
                                        />
                                    )}
                                </div>
                            }
                        >
                            <Column field="planName" header="Plan" />
                            <Column field="period" header="Period" 
                                body={(rowData) => formatPeriod(rowData.period)}
                            />
                            <Column field="startDay" header="Start Day"
                                body={(rowData) => renderConditionalField(rowData, 'startDay')}
                            />
                            <Column field="startTime" header="Start Time"
                                body={(rowData) => formatTime(rowData.startTime)}
                            />
                            <Column field="priority" header="Priority" 
                                body={(rowData) => rowData.priority || 1} 
                            />
                            <Column 
                                field="enabled" 
                                header="Enabled" 
                                body={(rowData) => (
                                    <Checkbox
                                        checked={rowData.enabled}
                                        disabled={true}
                                    />
                                )}
                                sortable 
                                filter 
                                filterElement={(options) => (
                                    <Checkbox
                                        checked={options.value}
                                        onChange={(e) => options.filterCallback(e.checked)}
                                    />
                                )}
                            />
                            <Column 
                                field="isAgentQueue" 
                                header="Agent Queue" 
                                body={(rowData) => (
                                    <Checkbox
                                        checked={rowData.isAgentQueue}
                                        disabled={true}
                                    />
                                )}
                                sortable 
                                filter 
                                filterElement={(options) => (
                                    <Checkbox
                                        checked={options.value}
                                        onChange={(e) => options.filterCallback(e.checked)}
                                    />
                                )}
                            />
                            <Column field="branch" header="Branch" 
                                sortable 
                                filter 
                                filterPlaceholder="Search branch"
                            />
                            <Column 
                                field="lastExecutionDate" 
                                header="Last Execution" 
                                sortable
                                body={(rowData) => formatLastExecutionDate(rowData.lastExecutionDate)}
                            />
                            {isAdmin && (
                                <Column
                                    header="Actions"
                                    body={(rowData) => (
                                        <div className="flex justify-content-center gap-2">
                                            <Button
                                                icon="pi pi-pencil"
                                                rounded
                                                outlined
                                                className="p-button-text"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onEditButtonClick(rowData);
                                                }}
                                                style={{ width: '2.5rem', height: '2.5rem' }}
                                            />
                                            <Button
                                                icon="pi pi-trash"
                                                rounded
                                                outlined
                                                severity="danger"
                                                className="p-button-text"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    confirmDelete(rowData, 'schedule');
                                                }}
                                                style={{ width: '2.5rem', height: '2.5rem' }}
                                            />
                                        </div>
                                    )}
                                />
                            )}
                        </DataTable>
                    </div>
                </div>
            </div>

            <Dialog
                visible={configDialogVisible}
                style={{ width: '60vw' }}
                header={`${isEditing ? 'Edit' : 'Add New'} ${configType === 'schedule' ? 'Scheduled' : 'Trigger'} Configuration`}
                modal
                className="edit-dialog"
                footer={
                    <div className="dialog-footer">
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            onClick={() => {
                                setConfigDialogVisible(false);
                                resetForm();
                            }} 
                            className="p-button-text p-button-sm mr-2" 
                        />
                        <Button 
                            label={isEditing ? 'Update' : 'Save'} 
                            icon="pi pi-check" 
                            onClick={saveConfig} 
                            className="p-button-primary p-button-sm" 
                        />
                    </div>
                }
                onHide={() => {
                    setConfigDialogVisible(false);
                    resetForm();
                }}
            >
                <div className="p-fluid edit-dialog-container">
                    {/* Plan Selection Section */}
                    <div className="section-header">Plan Selection</div>
                    <div className="p-grid p-formgrid">
                        <div className="p-col-12">
                            <div className="p-field">
                                <label htmlFor="plan">Jenkins Plan</label>
                                <Dropdown
                                    id="plan"
                                    value={selectedPlan?._id}
                                    options={jenkinsPlans.map(plan => ({
                                        label: plan.name,
                                        value: plan._id
                                    }))}
                                    onChange={(e) => {
                                        const plan = jenkinsPlans.find(p => p._id === e.value);
                                        setSelectedPlan(plan);
                                        setParamValues({});
                                    }}
                                    disabled={isEditing}
                                    placeholder="Select a Jenkins Plan"
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Configuration Settings Section */}
                    <div className="section-header">Configuration Settings</div>
                    <div className="p-grid p-formgrid">
                        {/* Add enabled checkbox at the top of Configuration Settings */}


                        {configType === 'schedule' ? (
                            <>
                                <div className="p-col-12 p-md-4">
                                    <div className="p-field">
                                        <label>Period</label>
                                        <Dropdown
                                            value={editingRowData.period}
                                            options={periodOptions}
                                            onChange={(e) => updateEditingRowData('period', e.value)}
                                            placeholder="Select Period"
                                            className="w-full"
                                        />
                                    </div>
                                </div>

                                {editingRowData.period?.toLowerCase() === 'weekly' && (
                                    <div className="p-col-12 p-md-4">
                                        <div className="p-field">
                                            <label>Start Day</label>
                                            <Dropdown
                                                value={editingRowData.startDay}
                                                options={dayOfWeekOptions}
                                                onChange={(e) => updateEditingRowData('startDay', e.value)}
                                                placeholder="Select Day"
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                )}

                                {editingRowData.period?.toLowerCase() === 'monthly' && (
                                    <div className="p-col-12 p-md-4">
                                        <div className="p-field">
                                            <label>Start Day</label>
                                            <InputNumber
                                                value={parseInt(editingRowData.startDay) || 1}
                                                onChange={(e) => updateEditingRowData('startDay', e.value.toString())}
                                                min={1}
                                                max={31}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                )}

                                {editingRowData.period?.toLowerCase() !== 'hourly' && (
                                    <div className="p-col-12 p-md-4">
                                        <div className="p-field">
                                            <label>Start Time</label>
                                            <Calendar
                                                value={editingRowData.startTime ? new Date(`2000-01-01T${editingRowData.startTime}`) : null}
                                                onChange={(e) => {
                                                    const time = e.value ? e.value.toTimeString().split(' ')[0].substring(0, 5) : null;
                                                    updateEditingRowData('startTime', time);
                                                }}
                                                timeOnly
                                                hourFormat="24"
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="p-col-12 p-md-6">
                                    <div className="p-field">
                                        <label htmlFor="branch">Branch</label>
                                        <InputText
                                            id="branch"
                                            value={editingRowData.branch || 'develop'}
                                            onChange={(e) => updateEditingRowData('branch', e.target.value)}
                                            required
                                            className="w-full"
                                        />
                                    </div>
                                </div>

                                <div className="p-col-12">
                                    <div className="field-checkbox mt-2">
                                        <Checkbox
                                            inputId="isAgentQueue"
                                            checked={editingRowData.isAgentQueue || false}
                                            onChange={(e) => updateEditingRowData('isAgentQueue', e.checked)}
                                        />
                                        <label htmlFor="isAgentQueue">{" "}Use Agent Queue</label>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="p-col-12 p-md-6">
                                    <div className="p-field">
                                        <label htmlFor="branchPattern">Branch Pattern</label>
                                        <InputText
                                            id="branchPattern"
                                            value={branchPattern}
                                            onChange={(e) => setBranchPattern(e.target.value)}
                                            placeholder="e.g., .*|master|feature/.*"
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                <div className="p-col-12">
                                    <div className="p-field">
                                        <label htmlFor="testSuites">Test Suites</label>
                                        <InputText
                                            id="testSuites"
                                            value={testSuites}
                                            onChange={(e) => setTestSuites(e.target.value)}
                                            placeholder="e.g., smoke,regression,integration"
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Add Priority field for both config types */}
                    <div className="p-col-12 p-md-4">
                        <div className="p-field">
                            <label>Priority</label>
                            <InputNumber
                                value={editingRowData.priority || 1}
                                onChange={(e) => updateEditingRowData('priority', e.value)}
                                min={1}
                                max={10000}
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="p-col-12">
                        <div className="field-checkbox mt-2">
                            <Checkbox
                                inputId="enabled"
                                checked={editingRowData.enabled ?? true}
                                onChange={(e) => updateEditingRowData('enabled', e.checked)}
                            />
                            <label htmlFor="enabled">{" "}Enabled</label>
                        </div>
                    </div>

                    {/* Jenkins Parameters Section */}
                    {selectedPlan && (
                        <>
                            <div className="section-header">Jenkins Parameters</div>
                            <div className="p-grid p-formgrid parameters-grid">
                                {selectedPlan.parameters.map((param, index) => (
                                    <div key={index} className="p-col-12 p-md-6 parameter-field">
                                        {renderParameterField(param)}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default ConfigTab;
